import { Head, Link } from "@inertiajs/react"
import { Building2, ExternalLink, Trophy, Users } from "lucide-react"

import { ClaimProfileDialog } from "@/components/claim-profile-dialog"
import AppLayout from "@/layouts/app-layout"
import { rankingPath, rankingsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Developer {
  id: number
  name: string
  headline: string | null
  location: string | null
  score: number | null
  linkedin_url: string
  company: {
    name: string
    tier: string
    tier_description: string
  } | null
}

interface Stats {
  total: number
  top_companies: Record<string, number>
}

interface Props {
  developers: Developer[]
  stats: Stats
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Rankings", href: rankingsPath() },
]

export default function RankingsIndex({ developers, stats }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Argentinian Developers Ranking" />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              Argentinian Developers Ranking
            </h1>
            <p className="text-muted-foreground">
              Top engineering talent from Argentina
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <Users className="text-muted-foreground h-5 w-5" />
              <span className="text-muted-foreground text-sm">
                Total Developers
              </span>
            </div>
            <p className="mt-2 text-3xl font-bold">{stats.total}</p>
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <Building2 className="text-muted-foreground h-5 w-5" />
              <span className="text-muted-foreground text-sm">Top Company</span>
            </div>
            <p className="mt-2 text-xl font-bold">
              {Object.keys(stats.top_companies)[0] || "N/A"}
            </p>
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <Trophy className="text-muted-foreground h-5 w-5" />
              <span className="text-muted-foreground text-sm">Avg Score</span>
            </div>
            <p className="mt-2 text-3xl font-bold">
              {developers.length > 0
                ? Math.round(
                    developers.reduce((sum, d) => sum + (d.score ?? 0), 0) /
                      developers.length,
                  )
                : 0}
            </p>
          </div>
        </div>

        <div className="rounded-xl border">
          <div className="border-b p-4">
            <h2 className="font-semibold">Developer Rankings</h2>
          </div>

          {developers.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center">
              No developers found. Rankings are updated daily.
            </div>
          ) : (
            <div className="divide-y">
              {developers.map((dev, index) => (
                <div
                  key={dev.id}
                  className="hover:bg-muted/50 flex items-center gap-3 px-4 py-3"
                >
                  <div className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                    {index + 1}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Link
                        href={rankingPath(dev.id)}
                        className="font-medium hover:underline"
                      >
                        {dev.name || "Unknown"}
                      </Link>
                      {dev.company && (
                        <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-xs">
                          {dev.company.name}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground truncate text-sm">
                      {dev.headline}
                    </p>
                  </div>

                  <div className="text-right">
                    <div className="font-bold">
                      {Math.round(dev.score ?? 0)}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {dev.location}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <a
                      href={dev.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:bg-muted text-muted-foreground hover:text-foreground inline-flex h-8 w-8 items-center justify-center rounded transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>

                    <ClaimProfileDialog
                      developerId={dev.id}
                      developerName={dev.name || "Unknown"}
                      variant="icon"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
